import { demoFunc } from './dees-harness-permission-card.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { harnessControlStyles } from '../harness.styles.js';
import type {
  IHarnessPermissionRequest,
  IHarnessPermissionViewState,
  THarnessPermissionResponse,
} from '../interfaces.js';
import { harnessStringifyValue } from '../harness.helpers.js';
import '../../00group-button/dees-button/dees-button.js';
import '../../00group-feedback/dees-badge/dees-badge.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-permission-card': DeesHarnessPermissionCard;
  }
}

const badgeTypeForPermission = (permissionType: string): 'error' | 'warning' | 'default' => {
  if (permissionType === 'delete' || permissionType === 'shell') return 'error';
  if (permissionType === 'write' || permissionType === 'browser') return 'warning';
  return 'default';
};

/**
 * Permission prompt card: title, type badge, metadata key/values, and the
 * once / always / reject choices with an optional remember checkbox. Emits
 * `harness-permission-response` (bubbles, composed) and disables itself
 * once answered or while `busy`.
 */
@customElement('dees-harness-permission-card')
export class DeesHarnessPermissionCard extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor request: IHarnessPermissionRequest = { id: '', title: '', type: 'permission' };

  @property({ type: Boolean })
  accessor busy: boolean = false;

  @property({ attribute: false })
  accessor viewState: IHarnessPermissionViewState | undefined = undefined;

  @state()
  accessor remember: boolean = false;

  /** local optimistic answer; request.response (host echo) takes precedence */
  @state()
  accessor answered: THarnessPermissionResponse | undefined = undefined;

  public get effectiveResponse(): THarnessPermissionResponse | undefined {
    return this.request.response ?? this.answered ?? this.viewState?.answered;
  }

  private get effectiveRemember(): boolean {
    return this.remember || this.viewState?.remember === true;
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessControlStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
        overflow: clip;
      }

      .card {
        padding: 16px;
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-bg-secondary);
      }

      .titleRow {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        margin-bottom: var(--dees-spacing-xs);
      }

      .titleRow dees-icon {
        color: var(--dees-color-accent-warning);
        padding: 5px;
        border-radius: var(--dees-radius-md);
        background: var(--dees-color-badge-warning-bg);
      }

      .title {
        flex: 1;
        min-width: 0;
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size, 13px);
        font-weight: 600;
      }

      .metaGrid {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
        gap: 6px 12px;
        margin: 12px 0;
        padding: 12px;
        border: 1px solid var(--dees-color-control-border);
        border-radius: var(--dees-radius-lg);
        background: var(--dees-color-control-bg);
        font-size: 12px;
      }

      .metaKey {
        color: var(--dees-color-text-secondary);
        white-space: nowrap;
      }

      .metaValue {
        color: var(--dees-color-text-primary);
        font-family: var(--dees-font-family-mono);
        overflow-wrap: anywhere;
      }

      .actions {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        flex-wrap: wrap;
      }

      .rememberLabel {
        display: inline-flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        margin-left: auto;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
        cursor: pointer;
        user-select: none;
      }

      .rememberLabel input { width: 16px; height: 16px; accent-color: var(--dees-color-accent-primary); }

      .answeredNote {
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
      }
    `,
  ];

  public render(): TemplateResult {
    const request = this.request;
    const disabled = this.busy || this.effectiveResponse !== undefined;
    const metadataEntries = Object.entries(request.metadata ?? {});
    return html`
      <div class="card">
        <div class="titleRow">
          <dees-icon icon="lucide:ShieldQuestionMark" iconSize="15"></dees-icon>
          <span class="title">${request.title || 'Permission requested'}</span>
          <dees-badge type=${badgeTypeForPermission(request.type)} .text=${request.type} rounded></dees-badge>
        </div>
        ${metadataEntries.length
          ? html`
              <div class="metaGrid">
                ${metadataEntries.map(([key, value]) => html`
                  <div class="metaKey">${key}</div>
                  <div class="metaValue">${harnessStringifyValue(value)}</div>
                `)}
              </div>
            `
          : ''}
        <div class="actions">
          ${this.effectiveResponse
            ? html`<span class="answeredNote">Answered: ${this.effectiveResponse}${this.effectiveRemember ? ' (remembered)' : ''}</span>`
            : html`
                <dees-button type="accent" size="sm" ?disabled=${disabled} @clicked=${() => this.respond('once')}>
                  Allow Once
                </dees-button>
                <dees-button size="sm" ?disabled=${disabled} @clicked=${() => this.respond('always')}>
                  Always Allow
                </dees-button>
                <dees-button type="destructive" size="sm" ?disabled=${disabled} @clicked=${() => this.respond('reject')}>
                  Reject
                </dees-button>
                <label class="rememberLabel">
                  <input
                    type="checkbox"
                    .checked=${this.effectiveRemember}
                    ?disabled=${disabled}
                      @change=${(event: Event) => {
                        this.remember = (event.currentTarget as HTMLInputElement).checked;
                        if (this.viewState) this.viewState.remember = this.remember;
                      }}
                  />
                  remember for this session
                </label>
              `}
        </div>
      </div>
    `;
  }

  private respond(response: THarnessPermissionResponse): void {
    if (this.busy || this.effectiveResponse !== undefined) return;
    const remember = this.effectiveRemember || response === 'always';
    this.answered = response;
    if (this.viewState) {
      this.viewState.answered = response;
      this.viewState.remember = remember;
    }
    this.dispatchEvent(
      new CustomEvent('harness-permission-response', {
        detail: {
          requestId: this.request.id,
          response,
          remember,
          request: this.request,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Focuses the first available response action for transcript-level navigation. */
  public async focusFirstAction(): Promise<boolean> {
    await this.updateComplete;
    if (this.busy || this.effectiveResponse !== undefined) return false;
    const action = this.shadowRoot?.querySelector<HTMLElement>('.actions dees-button');
    if (!action) return false;
    action.focus();
    return true;
  }
}
